import styled, { css } from 'styled-components';
import { applyStyleModifiers, type ModifiersConfig, type ModifierKeys } from 'styled-components-modifiers';
import { rgba } from 'polished';

// Import types
import type { Modifiers } from 'types/views/styles';
import type { ButtonVariant } from 'types/views/buttons';

// Import styles
import { flex, transitionMain, customMinResponsive, size } from 'components/styles';

const MODIFIERS: Modifiers = {
	outlined: ({ theme, variant }) => css`
		background: none;
		border: 1px ${rgba(theme.palette[variant].main, 0.5)} solid;
		color: ${theme.palette[variant].main};

		.ant-spin-dot-item {
			background: ${theme.palette[variant].main};
		}

		&:hover {
			background: none;
			color: ${theme.palette[variant].dark};
			border-color: ${rgba(theme.palette[variant].dark, 0.5)};
		}

		&:focus-visible {
			outline: none;
			background: ${rgba(theme.palette[variant].main, 0.1)};
		}

		&:disabled {
			background: none;
			color: ${theme.palette.secondary.dark};
			border-color: ${rgba(theme.palette.secondary.dark, 0.5)};

			.ant-spin-dot-item {
				background: ${theme.palette.secondary.dark};
			}
		}
	`,
	text: ({ theme, variant }) => css`
		background: none;
		border: none;
		color: ${theme.palette[variant].main};

		.ant-spin-dot-item {
			background: ${theme.palette[variant].main};
		}

		&:hover {
			background: none;
			color: ${theme.palette[variant].dark};
		}

		&:focus-visible {
			outline: none;
			background: ${rgba(theme.palette[variant].main, 0.1)};
		}

		&:disabled {
			background: none;
			color: ${theme.palette.secondary.dark};

			.ant-spin-dot-item {
				background: ${theme.palette.secondary.dark};
			}
		}
	`,
	small: () => css`
		height: 3.8rem;
		padding: 0.6rem 1.6rem;
		font-size: 1.5rem;
	`,
	auth_link: () => css`
		font-size: 1.4rem;
		height: 4.6rem;
	`,
	underline: () => css`
		text-decoration: underline;
	`,
	max_content: () =>
		customMinResponsive(
			size.semi,
			css`
				width: max-content;
			`
		)
};

export const StyledButton = styled.button<{ modifiers?: ModifierKeys; variant: ButtonVariant }>`
	cursor: pointer;
	width: 100%;
	height: 5rem;
	padding: 1rem 2.4rem;
	${flex()}
	column-gap: 1rem;
	border: none;
	border-radius: 10rem;
	background: ${({ theme, variant }) => theme.palette[variant].main};
	color: ${({ theme, variant }) => (variant === 'primary' ? theme.palette.white : rgba(theme.palette.black, 0.9))};
	font-weight: ${({ theme }) => theme.font.distinct};
	letter-spacing: 0.16px;
	${transitionMain()}

	.ant-spin-dot-item {
		background: ${({ theme, variant }) => (variant === 'primary' ? theme.palette.white : rgba(theme.palette.black, 0.9))};
	}

	svg {
		height: 80%;
	}

	&:hover {
		background: ${({ theme, variant }) => theme.palette[variant].dark};
	}

	&:focus-visible {
		outline: 1px solid ${({ theme, variant }) => theme.palette[variant].main};
		outline-offset: 0.2rem;
	}

	&:disabled {
		cursor: not-allowed;
		color: ${({ theme }) => theme.palette.white};
		background: ${({ theme }) => theme.palette.secondary.dark};

		.ant-spin-dot-item {
			background: ${({ theme }) => theme.palette.black};
		}
	}

	${applyStyleModifiers(MODIFIERS as ModifiersConfig)};
`;
